// src/connection/sessionContinuity.ts — the AI panel's side of WS session continuity
// (agent-service Terms: Bound Session, Reconnect Resume — omega-ai-agent-service
// pages/agent-service/terms.html). Pure: AiConnectionProvider feeds it the Bound Session id and
// the server frames, and dispatches/sends what it returns. Kept free of React so the rules are
// unit-tested directly (sessionContinuity.test.ts).
import { ActionType } from "../actionTypes";
import { AiEvent, AiEventType } from "../types/ai-event";
import type { AiIdentity } from "../slices/ui";
import { chatPhase } from "./chatPhase";

export const RESUME_FAILED_MESSAGE =
  "Your previous conversation couldn't be restored, so Cas won't remember it. Your next message starts a new conversation.";

// A resume that fails because of the Devcontainer (not because the Session is gone): the conversation is
// intact, so these say why it cannot be opened now and that a message typed now starts a new one.
export type ResumeFailedCode = "devcontainer-conflict" | "volume-in-use";
export const RESUME_BLOCKED_MESSAGES: Record<ResumeFailedCode, string> = {
  "devcontainer-conflict":
    "This chat can't be opened right now: its agent is running on a different devcontainer than this chat was started on. The conversation is intact. It can be opened again when its account's agent is not running on another devcontainer. A message typed now starts a new conversation.",
  "volume-in-use":
    "This chat can't be opened right now: its devcontainer's volume is in use by another account's agent. The conversation is intact; open it again from the list later. A message typed now starts a new conversation.",
};

// A frame the socket could not carry is said so in the chat, never dropped silently
// (agent-chat-speed Decision 5, "Error handling").
export const NOT_SENT_MESSAGE = "Not connected to Cas — your message was not sent. Send it again once Cas reconnects.";
export const RESUME_NOT_SENT_MESSAGE = "Not connected to Cas — this conversation could not be opened. Open it again once Cas reconnects.";
export const HISTORY_NOT_SENT_MESSAGE = "Not connected to Cas.";
export const DROPPED_BEFORE_START_MESSAGE =
  "The connection dropped while your workspace was starting. Send your message again.";

// What to do the moment a socket opens. With a Bound Session, resume it — never let the next
// message mint a new Session behind a transcript that still looks continuous. The on-screen
// transcript is cleared because the server replays the Session's own Transcript (the truth,
// including anything that happened while the socket was down), and a local provisioning status
// ("Starting workspace…", agent-chat-speed Decision 5) holds the composer until the server's
// replay ends (status_idle) or fails (session.resume_failed clears it), so no message can race the
// resume.
export function resumeOnOpen(activeSessionId: string | null, now: number): {
  actions: any[];
  frames: Array<{ type: "session.resume"; sessionId: string }>;
} {
  if (!activeSessionId) return { actions: [], frames: [] };
  return {
    actions: [
      { type: ActionType.CLEAR_AI_EVENTS },
      { type: ActionType.SET_AI_CHAT_LOADING, loading: "resuming" },
      { type: ActionType.ADD_AI_EVENT, event: { type: AiEventType.STATUS_PROVISIONING, timestamp: now } },
    ],
    frames: [{ type: "session.resume", sessionId: activeSessionId }],
  };
}

// Redux actions for the continuity frames. session.bound (a new Session was minted) and
// session.resumed make that Session the Bound one; session.reset (the server finished a reset —
// serialized after any frame that was still in flight) and session.resume_failed unbind it; a
// failed resume also clears the stale transcript and says so in the log — the reset is shown,
// never hidden.
export function continuityActions(data: any, now: number): any[] {
  switch (data?.type) {
    case "session.bound":
    case "session.resumed":
      return [{ type: ActionType.SET_ACTIVE_SESSION_ID, sessionId: data.sessionId }];
    case "session.reset":
      return [
        { type: ActionType.SET_ACTIVE_SESSION_ID, sessionId: null },
        { type: ActionType.SET_AI_CHAT_LOADING, loading: null },
      ];
    case "session.resume_failed": {
      const blocked = typeof data.code === "string" ? RESUME_BLOCKED_MESSAGES[data.code as ResumeFailedCode] : undefined;
      return [
        { type: ActionType.CLEAR_AI_EVENTS },
        { type: ActionType.SET_ACTIVE_SESSION_ID, sessionId: null },
        { type: ActionType.SET_AI_CHAT_LOADING, loading: null },
        {
          type: ActionType.ADD_AI_EVENT,
          event: { type: AiEventType.SESSION_ERROR, error: blocked ?? `${RESUME_FAILED_MESSAGE} (${data.error})`, timestamp: now },
        },
      ];
    }
    default:
      return [];
  }
}

// A resume that finds the Session idle ends the busy status the resume itself set, even if the server's closing status_idle
// never arrives (a frame lost between two quick reconnects): the chat would otherwise stay busy for good while the header says Connected.
// Returns the idle to add, or null when nothing is pending. A later server status_idle is harmless: chatPhase is already idle.
export function resumedIdleEvent(data: any, events: AiEvent[], now: number): AiEvent | null {
  if (data?.type !== "session.resumed" || data.busy !== false) return null;
  return chatPhase(events).busy ? { type: AiEventType.STATUS_IDLE, timestamp: now } : null;
}

// Is a Run (or the workspace start in front of it) in progress, by the panel's own events? The
// same rule ChatSurface uses for its composer — chatPhase.ts.
export function runInProgress(events: AiEvent[]): boolean {
  return chatPhase(events).busy;
}

// The Account an AI-panel identity acts as — the agent-service's Account key (Terms: Account):
// a service account is `service-account:<id>`, "My account" is the logged-in user. Switching to a
// DIFFERENT Account must start a new chat; re-picking the same Account must not.
export function identityKey(identity: AiIdentity): string {
  return identity.kind === "service-account" ? `service-account:${identity.serviceAccountId}` : "self";
}

// The credential the socket URL carries for an identity. Only a change of credential needs a new
// socket; re-picking the identity already in use must not tear the connection down.
export function identityCredentialKey(identity: AiIdentity): string {
  return identity.kind === "service-account" ? `service-account:${identity.serviceAccountId}:${identity.apiKey}` : "self";
}
